import { Snackbar } from "@dropbox/dig-components/snackbar";
import { UIIcon } from "@dropbox/dig-icons";
import { FailLine } from "@dropbox/dig-icons/assets";
import { TemplateSettingsModal } from "@mirage/mosaics/ComposeAssistant/components/settings/TemplateSettingsModal";
import { SnackbarView } from "@mirage/shared/snackbar/SnackbarView";
import { useCallback, useState } from "react";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { AssistCustomTemplate } from "@mirage/shared/compose/assist-template";

export interface TemplateSettingsModalConfig {
  type: "open" | "empty_state";
  templateID?: string;
}

interface UseTemplateSettingsModalProps {
  templates: AssistCustomTemplate[];
  currentTemplateID?: string | null;
  saveTemplate: (template: AssistCustomTemplate) => Promise<string>;
  deleteTemplate: (templateId: string) => void;
  postUpdateDraftWithTemplate: (templateId: string) => void;
  onTemplateChange?: (templateID: string | null, templateName: string) => void;
  logTemplatesPaneEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
      currentSessionID?: string;
    },
  ) => void;
}

export function useTemplateSettingsModal({
  templates,
  currentTemplateID,
  saveTemplate,
  deleteTemplate,
  postUpdateDraftWithTemplate,
  onTemplateChange,
  logTemplatesPaneEvent,
}: UseTemplateSettingsModalProps) {
  const [openTemplateSettings, setOpenTemplateSettings] = useState<
    TemplateSettingsModalConfig | undefined
  >();

  const [createdTemplateSnackbarMsg, setCreatedTemplateSnackbarMsg] = useState<
    undefined | string
  >();

  const handleCloseTemplatesSettings = useCallback(() => {
    setOpenTemplateSettings(undefined);
  }, []);

  const handleSaveTemplate = useCallback(
    async (template: AssistCustomTemplate) => {
      const id = await saveTemplate(template);
      return id;
    },
    [saveTemplate],
  );

  const handleDeleteTemplate = useCallback(
    (templateId: string) => {
      deleteTemplate(templateId);
      setOpenTemplateSettings(undefined);
    },
    [deleteTemplate],
  );

  const handleChangeTemplate = useCallback(
    (templateID: string, templateName: string) => {
      postUpdateDraftWithTemplate(templateID);
      handleCloseTemplatesSettings();
      if (openTemplateSettings?.type === "empty_state" && onTemplateChange) {
        onTemplateChange(templateID, templateName);
      }
    },
    [
      postUpdateDraftWithTemplate,
      handleCloseTemplatesSettings,
      onTemplateChange,
      openTemplateSettings,
    ],
  );

  const handleSetCreatedTemplateSnackbarMsg = useCallback(
    (msg: string | undefined) => {
      setCreatedTemplateSnackbarMsg(msg);
    },
    [],
  );

  const templateSettingsSnackbar = createdTemplateSnackbarMsg && (
    <Snackbar.Position>
      <SnackbarView
        title={createdTemplateSnackbarMsg}
        onClose={() => setCreatedTemplateSnackbarMsg(undefined)}
        accessory={<UIIcon src={FailLine} />}
        timeoutMs={3000}
      />
    </Snackbar.Position>
  );

  const templateSettingsModal = (openTemplateSettings?.type === "open" ||
    openTemplateSettings?.type === "empty_state") && (
    <TemplateSettingsModal
      isOpen={true}
      templates={templates}
      onRequestClose={handleCloseTemplatesSettings}
      logComposeEvent={logTemplatesPaneEvent}
      saveTemplate={handleSaveTemplate}
      onDeleteTemplate={handleDeleteTemplate}
      onUseTemplate={handleChangeTemplate}
      currentTemplateID={currentTemplateID}
      setCreatedTemplateSnackbarMsg={handleSetCreatedTemplateSnackbarMsg}
      templateSettingsSnackbar={templateSettingsSnackbar}
    />
  );
  return {
    openTemplateSettings,
    setOpenTemplateSettings,
    templateSettingsModal,
  };
}
